import { create } from 'zustand'
import { io } from 'socket.io-client'
import toast from 'react-hot-toast/headless'
import soundController from '../sound/SoundController'
import { auth } from '../lib/firebase'
import i18n from '../i18n'

// const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'http://localhost:5001'
// import { SOCKET_URL } from '../config';
// import { SOCKET_URL } from '../../config';
import { SOCKET_URL } from '@config';

export const useSocket = create((set, get) => ({
  socket: null,
  connected: false,
  onlineUsers: [],
  games: [],
  currentGame: null,
  rejoinSnapshot: null, // { gameId, gameState, currentPhase } from gameRejoined
  myHand: [], // Knock Knock: private hand of cards from hand_dealt/hand_update
  messages: [],
  unreadMessages: 0, // messages received while the chat panel is closed
  readyPlayers: [],
  showCountdown: false,
  countdownData: null,
  availableGameTypes: [],

  connect: (user) => {
    if (get().socket) {
      get().socket.disconnect()
    }

    const socket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      withCredentials: true,
      // Function form: Socket.IO calls this before every (re)connect
      // attempt, so a fresh Firebase ID token (they expire hourly) is
      // always sent instead of a stale one captured at connect() time.
      auth: async (cb) => {
        const idToken = await auth.currentUser?.getIdToken()
        cb({ idToken })
      }
    });

    socket.on('connect', () => {
      set({ connected: true })
    })

    socket.on('disconnect', () => {
      set({ connected: false })
    })

    socket.on('connected', (data) => {
      // Connection confirmed
    })

    socket.on('userList', (users) => {
      set({ onlineUsers: users })
    })

    socket.on('gameList', (games) => {
      set({ games })
    })

    socket.on('availableGameTypes', (gameTypes) => {
      set({ availableGameTypes: gameTypes })
    })

    socket.on('gameCreated', (game) => {
      set({ currentGame: game })
      soundController.play('game_launched')
      toast(i18n.t('toasts.gameCreated', { ns: 'lobby' }))
    })

    socket.on('gameJoined', (game) => {
      set({ currentGame: game })
      soundController.play('game_launched')
      toast(i18n.t('toasts.joinedGame', { ns: 'lobby', name: game.name }))
    })

    socket.on('playerJoined', ({ playerId, playerName, players }) => {
      const currentGame = get().currentGame
      if (currentGame) {
        set({
          currentGame: {
            ...currentGame,
            players
          }
        })
        soundController.play('player_joined')
        toast(playerName
          ? i18n.t('toasts.playerJoined', { ns: 'lobby', name: playerName })
          : i18n.t('toasts.playerJoinedGeneric', { ns: 'lobby' }))
      }
    })

    socket.on('playerLeft', ({ playerId, playerName, players }) => {
      const currentGame = get().currentGame
      if (currentGame) {
        set({
          currentGame: {
            ...currentGame,
            players
          }
        })
        soundController.play('player_disconnected')
        toast(playerName
          ? i18n.t('toasts.playerLeft', { ns: 'lobby', name: playerName })
          : i18n.t('toasts.playerLeftGeneric', { ns: 'lobby' }))
      }
    })

    // All human players left this game (only bots, if any, remain) — the
    // server already auto-closed it. Send everyone still viewing it back
    // to the lobby. `message` is the server's (hardcoded English) text,
    // kept only as a fallback if a future server build omits messageKey.
    socket.on('game_closed', ({ message, messageKey, ns }) => {
      const currentGame = get().currentGame
      if (currentGame) {
        set({ currentGame: null, rejoinSnapshot: null, myHand: [] })
        toast(messageKey ? i18n.t(messageKey, { ns: ns || 'lobby' }) : (message || i18n.t('toasts.gameClosed', { ns: 'lobby' })))
      }
    })

    // Mid-game reconnect: the server held our seat during the disconnect
    // grace window and sent a state snapshot. Stash the snapshot in the
    // store too: the event often arrives BEFORE DynamicGameLoader has
    // mounted (page reload → navigation still in flight), so its own
    // socket handler can miss it — on mount it reads this instead.
    socket.on('gameRejoined', ({ game, gameState, currentPhase }) => {
      set({
        currentGame: game,
        rejoinSnapshot: { gameId: game.id, gameState, currentPhase }
      })
      toast(i18n.t('toasts.reconnectedToGame', { ns: 'lobby' }))
    })

    // Knock Knock: private hand delivery (initial deal + mid-game updates
    // + reconnect resend all use these two events — see game.js). Same
    // race as gameRejoined above: the reconnect-time emission can arrive
    // before DynamicGameLoader has mounted its own listeners, so it's
    // captured here instead, where the socket connects.
    socket.on('hand_dealt', ({ hand }) => {
      set({ myHand: hand })
    })

    socket.on('hand_update', ({ hand }) => {
      set({ myHand: hand })
    })

    socket.on('playerConnectionStatus', ({ username, status, graceSeconds }) => {
      if (status === 'disconnected') {
        toast(i18n.t('toasts.playerDisconnectedGrace', { ns: 'lobby', username, graceSeconds }))
      } else if (status === 'reconnected') {
        toast(i18n.t('toasts.playerReconnected', { ns: 'lobby', username }))
      }
    })

    socket.on('gameStarted', (game) => {
      set({ currentGame: game })
      soundController.play('game_started')
      toast(i18n.t('toasts.gameStarted', { ns: 'lobby' }))
    })

    socket.on('hostChanged', ({ newHost }) => {
      const currentGame = get().currentGame
      if (currentGame) {
        set({
          currentGame: {
            ...currentGame,
            host: newHost
          }
        })
        toast(i18n.t('toasts.hostChanged', { ns: 'lobby' }))
      }
    })

    socket.on('message', (message) => {
      set(state => ({
        messages: [...state.messages, message],
        unreadMessages: state.unreadMessages + 1
      }))
      // Chime only for other people's messages, not our own echo
      if (message.from !== user.id) {
        soundController.play('message_received')
      }
    })

    // Recent history sent on connect / game join. Merge and dedupe by id:
    // a reconnect can replay messages we already hold, and global + game
    // history arrive as separate events.
    socket.on('chatHistory', ({ messages: history }) => {
      set(state => {
        const seen = new Set(state.messages.map(m => m.id))
        const fresh = history.filter(m => !seen.has(m.id))
        if (fresh.length === 0) return {}
        const merged = [...state.messages, ...fresh]
          .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
        return { messages: merged }
      })
    })

    // messageKey/ns/messageParams let specific errors carry a translation
    // key (with interpolation values) instead of fixed English text —
    // everything else here still just toasts the raw (hardcoded English)
    // `message` the server sends, unchanged.
    socket.on('error', ({ message, messageKey, ns, messageParams }) => {
      toast(messageKey ? i18n.t(messageKey, { ns: ns || 'common', ...messageParams }) : message)
    })

    // Admin banned this account/IP while the socket was live — the server
    // force-disconnects right after this, so just surface why.
    socket.on('banned', ({ message }) => {
      toast.error(message || i18n.t('youHaveBeenBanned', { ns: 'common' }))
    })

    socket.on('quickJoinFound', ({ gameId }) => {
      get().joinGame(gameId)
    })

    socket.on('quickJoinNotFound', () => {
      toast(i18n.t('toasts.quickJoinNotFound', { ns: 'lobby' }))
    })

    socket.on('playerReadyChanged', ({ gameId, playerId, isReady, readyPlayers }) => {
      const currentGame = get().currentGame
      if (currentGame && currentGame.id === gameId) {
        set({ readyPlayers })

        // Only show toast if it's about a specific player changing status
        if (playerId && typeof isReady === 'boolean') {
          if (isReady) {
            soundController.play('player_ready')
          }
          const status = i18n.t(isReady ? 'ready' : 'notReady', { ns: 'common' })
          toast(i18n.t('toasts.playerReadyStatus', { ns: 'lobby', playerId, status }))
        }
      }
    })

    socket.on('gameStartCountdown', (data) => {
      set({
        showCountdown: true,
        countdownData: data
      })
    })

    set({ socket })
  },

  disconnect: () => {
    const socket = get().socket
    if (socket) {
      socket.disconnect()
      set({ socket: null, connected: false, currentGame: null })
    }
  },

  createGame: (gameData) => {
    const socket = get().socket
    if (socket) {
      socket.emit('createGame', gameData)
    }
  },

  joinGame: (gameId, password = '') => {
    const socket = get().socket
    if (socket) {
      socket.emit('joinGame', { gameId, password })
    }
  },

  leaveGame: () => {
    const socket = get().socket
    if (socket) {
      socket.emit('leaveGame')
      set({ currentGame: null, rejoinSnapshot: null, myHand: [] })
    }
  },

  startGame: (gameId) => {
    const socket = get().socket
    if (socket) {
      socket.emit('startGameInstance', { gameId })
    }
  },

  sendMessage: (type, content, gameId = null, recipient = null) => {
    const socket = get().socket
    if (socket) {
      socket.emit('sendMessage', { type, content, gameId, recipient })
    }
  },

  quickJoin: (gameType = null, skillLevel = 'medium') => {
    const socket = get().socket
    if (socket) {
      socket.emit('quickJoin', { gameType, skillLevel })
    }
  },

  updateStatus: (status) => {
    const socket = get().socket
    if (socket) {
      socket.emit('updateStatus', status)
    }
  },

  toggleReady: (gameId) => {
    const socket = get().socket
    if (socket) {
      socket.emit('toggleReady', { gameId })
    }
  },

  hideCountdown: () => {
    set({ showCountdown: false, countdownData: null })
  },

  markMessagesRead: () => {
    if (get().unreadMessages !== 0) {
      set({ unreadMessages: 0 })
    }
  }
}))